const saState = {
  isLoggedIn: false,
  clientId: null,
  clientType: null, // 0 - always free, 1- multy, 2- profy
  clientCode: null, // Added for client registration
  nick: null,
  nicks: [], // List of nicknames for the client
  is_owner: false, // Added to track if the user(nick) is a client's owner
  selectedProfileId: null, // Added to track the currently selected profile
  accessToken: null,
  profiles: [],
  foods: [],
  lastUpdated: null, // Timestamp of the last update
}

const STORAGE_KEY = 'samappState';

// This function initializes the state manager by loading 
// the saved state from localStorage
export function saveState() {
  setLastUpdated(new Date().toISOString());
  localStorage.setItem(STORAGE_KEY, JSON.stringify(saState));
}

// This function loads the state from localStorage and merges
// it with the current saState object
export function loadState() {
  try {
    const state = localStorage.getItem(STORAGE_KEY);
    if (state) Object.assign(saState, JSON.parse(state));
  } catch (e) {
    console.warn("Failed to load state:", e);
    clearState();
  }
}

// This function clears the saved state from localStorage
export function clearState() {
  localStorage.removeItem(STORAGE_KEY);
  Object.assign(saState, {
    isLoggedIn: false,
    clientId: null,
    nick: null, // Nick the device owner
    nicks: [], // The client's nicknames
    accessToken: null,
    profiles: [],
    foods: [],
  });
}

// This function debounces the saveState function to prevent
// excessive writes to localStorage. It will only save the state
// after 300 milliseconds of inactivity.
// This is useful for performance optimization, especially when
// multiple updates are made in quick succession.
// It prevents the state from being saved too frequently, which can
// lead to performance issues or excessive writes to localStorage.
// It uses a timer to delay the save operation until the user has stopped
// making changes for a specified period (300 milliseconds in this case).
let saveTimer;
function debounceSave() {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(saveState, 300);
}

// This function updates the state with new values
export function updateState(newValues) {
  Object.assign(saState, newValues);
  saveState();
}

export function updateStateDebounced(newValues) {
  Object.assign(saState, newValues);
  debounceSave();
}

// This function retrieves the current state
export function getState() {
  return { ...saState }; // Return a shallow copy to prevent direct mutations
}

export function getLastUpdated() {
  return saState.lastUpdated;
}

function setLastUpdated(timestamp) {
  saState.lastUpdated = timestamp;
}

// This function checks if the user is logged in
export function isLoggedIn() {
  return saState.isLoggedIn;
}

// This function sets the logged-in status
export function setLoggedIn(status) {
  saState.isLoggedIn = status;
  saveState();
}

// This function sets the client ID
export function setClientId(clientId) {
  saState.clientId = clientId;
  saveState();
}

// This function retrieves the client ID
export function getClientId() {
  return saState.clientId;
}

// This function sets the client code
export function setClientCode(clientCode) {
  saState.clientCode = clientCode;
  saveState();
}

// This function retrieves the client code
export function getClientCode() {
  return saState.clientCode;
}

// This function sets the client type
export function setClientType(clientType) {
  saState.clientType = clientType;
  saveState();
}

// This function retrieves the client type
export function getClientType() {
  return saState.clientType;
}


// This function sets the nickname
export function setNick(nick) {
  saState.nick = nick;
  saveState();
}

// This function can be used to get the nickname
export function getNick() {
  return saState.nick;
}

// This function sets the owner status
export function setIsOwner(isOwner) {
  saState.is_owner = isOwner;
  saveState();
}

// This function retrieves the owner status
export function isOwner() {
  return saState.is_owner;
}

// This function sets the selected profile ID
export function setSelectedProfileId(profileId) {
  saState.selectedProfileId = profileId;
  saveState();
}

// This function retrieves the selected profile ID
export function getSelectedProfileId() {
  return saState.selectedProfileId;
}

// This function sets the access token
export function setAccessToken(token) {
  saState.accessToken = token;
  saveState();
}

// This function retrieves the access token
export function getAccessToken() {
  return saState.accessToken;
}

// This function retrieves the nicks
export function getNicks() {
  return saState.nicks || [];
}

// This function sets the nicks
export function setNicks(nicks) {
  saState.nicks = nicks;
  saveState();
}

// This function sets all profiles
export function setProfiles(profiles) {
  saState.profiles = profiles;
  saveState();
}

// This function retrieves all profiles
export function getProfiles() {
  return saState.profiles;
}

export function getProfileById(profileId) {
  return saState.profiles.find(profile => profile.id === profileId) || null;
}

export function getSelectedProfile() {
  const profileId = getSelectedProfileId();
  return getProfileById(profileId);
}

// This function sets all food items
export function setFoods(foods) {
  saState.foods = foods;
  saveState();
}

// This function retrieves all food items
export function getFoods() {
  return saState.foods;
}

// This function initializes the state manager
// Call this function to initialize the state manager when the app starts
// initializeStateManager();
export function initializeStateManager() {
  console.log('Initializing state manager...');
  loadState();
  // Additional initialization logic can be added here if needed
}

// This function can be used to reset the state manager
export function resetStateManager() {
  clearState();
  initializeStateManager();
}

// This function can be used to check if the state manager is initialized
export function isStateManagerInitialized() {
  return Object.keys(saState).length > 0;
}


// This function can be used to check if the state manager has any profiles
export function hasProfiles() {
  return saState.profiles.length > 0;
}

// This function can be used to check if the state manager has any food items
export function hasFoods() {
  return saState.foods.length > 0;
}
// This function can be used to check if the state manager has a client ID
export function hasClientId() {
  return saState.clientId !== null;
}
// This function can be used to check if the state manager has a nickname
export function hasNick() {
  return saState.nick !== null;
}

// This function can be used to check if the state manager has an access token
export function hasAccessToken() {
  return saState.accessToken !== null;
}

// This function can be used to check if the state manager is logged in
export function isUserLoggedIn() {
  return saState.isLoggedIn;
}

// This function can be used to set the logged-in status
export function setUserLoggedIn(status) {
  setLoggedIn(status);
}
